import type {
  AssistantMessagesPage,
  AssistantSession,
} from '@/lib/api/assistant.api'
import type { InfiniteData } from '@tanstack/react-query'
import { useMemo } from 'react'
import { assistantApi } from '@/lib/api/assistant.api'
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

import { queryKeys } from '../query-keys'

export function useAssistantSessionsQuery(options?: { enabled?: boolean }) {
  return useQuery<AssistantSession[]>({
    queryKey: queryKeys.assistant.sessions(),
    queryFn: () => assistantApi.listSessions(),
    enabled: options?.enabled !== false,
    // Short staleness + focus refetch: conversations are edited from any open
    // tab (rename, delete), and a tab coming back to the foreground must not
    // keep showing rows another tab already removed.
    staleTime: 30 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: true,
  })
}

export function useAssistantMessagesQuery(
  sessionId: string | undefined,
  options?: { enabled?: boolean },
) {
  return useInfiniteQuery({
    queryKey: queryKeys.assistant.messages(sessionId ?? ''),
    queryFn: ({ pageParam }) =>
      assistantApi.getMessages(sessionId!, {
        cursor: pageParam,
        limit: 20,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: lastPage =>
      lastPage.hasMore ? (lastPage.nextCursor ?? undefined) : undefined,
    enabled: options?.enabled !== false && !!sessionId,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
}

export function useAssistantInvalidation() {
  const queryClient = useQueryClient()

  // Stable reference across renders — callers put these in useCallback deps,
  // so a fresh object literal each render would invalidate every downstream
  // memo (LatestExchangeView → MessageBubble → ReactMarkdown components),
  // causing visible re-mounts of streamed content like ModuleCardList.
  return useMemo(
    () => ({
      invalidateSessions: () =>
        queryClient.invalidateQueries({
          queryKey: queryKeys.assistant.sessions(),
        }),
      invalidateMessages: (sessionId: string) =>
        queryClient.invalidateQueries({
          queryKey: queryKeys.assistant.messages(sessionId),
        }),
      setMessages: (sessionId: string, page: AssistantMessagesPage) =>
        queryClient.setQueryData<
          InfiniteData<AssistantMessagesPage, string | undefined>
        >(queryKeys.assistant.messages(sessionId), {
          pages: [page],
          pageParams: [undefined],
        }),
      setSessionTitle: (sessionId: string, title: string) => {
        queryClient.setQueryData<AssistantSession[]>(
          queryKeys.assistant.sessions(),
          old =>
            old?.map(s => (s.id === sessionId ? { ...s, title } : s)),
        )
      },
      removeSession: (sessionId: string) => {
        queryClient.removeQueries({
          queryKey: queryKeys.assistant.messages(sessionId),
        })
        queryClient.setQueryData<AssistantSession[]>(
          queryKeys.assistant.sessions(),
          old => old?.filter(s => s.id !== sessionId),
        )
      },
    }),
    [queryClient],
  )
}
